import {
  InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient
} from "@tanstack/react-query";
import endOfDay from "date-fns/endOfDay";

import {
  IBaseMatchData,
  IMatch,
  ISavedSearchPreference,
  ISearchPreferencePayload,
  IUpdateCheckoutDatePayload,
  TAggregatedMatchData,
  TAggregatedMatchesResponse,
  TBaseReview,
  TMatchData,
  TReview,
  TStudentActionExtrasPayload
} from "../../interfaces/interfaces";
import { StudentAPI } from "../studentApi";
import { getHouseQueryKey, getHousesQueryKey } from "./houseApiQueries";
import { useContextHospi } from "../../context/ContextHospi";
import {
  getReviewsByHostQueryKey,
  getReviewsByStudentQueryKey
} from "./userApiQueries";
import { useContextStudentSearches } from "../../context/ContextStudentSearches";
import { sortByCreationTime } from "../../utils/time";
import {
  MatchStatusEnum,
  SHOWN_MATCHES_FROM_STATUS
} from "../../utils/constants";
import { isoDate } from "../../utils/helpers";

export const useStudentAcceptInvite = () => {
  const queryClient = useQueryClient();
  const { currentUserId } = useContextHospi();

  return useMutation<
    unknown,
    unknown,
    {
      payload: IBaseMatchData;
      offeredDates?: Pick<
        IMatch,
        "offered_check_in_date" | "offered_check_out_date"
      >;
    }
  >({
    mutationFn: ({ payload }) => StudentAPI.acceptInvite(payload),
    onSuccess: (_, { payload, offeredDates }) => {
      if (!offeredDates) {
        queryClient.invalidateQueries({
          queryKey: getStudentAggregatedMatchesQueryKey({
            userId: currentUserId
          })
        });
        queryClient.invalidateQueries({
          queryKey: getStudentMatchesQueryKey({ userId: currentUserId })
        });
      } else {
        const updatedMatchData = {
          match_status: MatchStatusEnum.ACCEPTED,
          offered_check_in_date: undefined,
          offered_check_out_date: undefined,
          most_recent_cancel_reason: null,
          check_in_date: offeredDates.offered_check_in_date,
          check_out_date: offeredDates.offered_check_out_date,
          student_action_extras: {
            is_pay_deposit_checked: false,
            is_host_ended_rental_period_checked: false,
            is_create_review_checked: false,
            is_withhold_deposit_proposal_checked: false
          }
        };

        queryClient.setQueryData<TMatchData[]>(
          getStudentMatchesQueryKey({ userId: currentUserId }),
          (oldData) => {
            if (oldData) {
              return oldData.map((m) => {
                if (
                  m.house_id === payload.house_id &&
                  m.host_id === payload.host_id
                ) {
                  return { ...m, ...updatedMatchData };
                } else return m;
              });
            }
          }
        );
        queryClient.setQueryData<InfiniteData<TAggregatedMatchesResponse>>(
          getStudentAggregatedMatchesQueryKey({ userId: currentUserId }),
          (oldData) => {
            if (oldData) {
              return {
                ...oldData,
                pages: oldData.pages.map((page) => ({
                  ...page,
                  data: page.data.reduce((acc, c) => {
                    if (
                      c.match.house_id === payload.house_id &&
                      c.match.host_id === payload.host_id
                    ) {
                      acc.push({
                        ...c,
                        match: { ...c.match, ...updatedMatchData }
                      });
                    } else acc.push(c);
                    return acc;
                  }, [] as TAggregatedMatchData[])
                }))
              };
            }
          }
        );
      }
      queryClient.invalidateQueries({
        queryKey: getMatchQueryKey({
          studentId: payload.student_id,
          hostId: payload.host_id,
          houseId: payload.house_id
        })
      });
      queryClient.invalidateQueries({
        queryKey: getHouseQueryKey(payload.house_id)
      });
      queryClient.invalidateQueries({
        queryKey: getHousesQueryKey(currentUserId!)
      });
      queryClient.setQueryData<ISavedSearchPreference[]>(
        getSearchesQueryKey({ studentId: payload.student_id }),
        (oldData) => {
          if (oldData)
            return oldData.map((s) => ({
              ...s,
              whatsapp_enabled: false,
              email_enabled: false
            }));
        }
      );
    }
  });
};

export const getMatchQueryKey = (params: {
  studentId?: string;
  hostId?: string;
  houseId?: string;
}) => ["match", JSON.stringify(params)];

export const useGetMatch = (
  studentId?: string,
  hostId?: string,
  houseId?: string,
  enabled?: boolean
) => {
  return useQuery(
    getMatchQueryKey({ studentId, hostId, houseId }),
    () => {
      if (!studentId || !hostId || !houseId) {
        return;
      }

      return StudentAPI.getMatch(studentId, hostId, houseId, {
        showNotificationOnError: false
      }).catch((e) => {
        if (e?.response?.status === 404) {
          return {
            student_id: studentId,
            host_id: hostId,
            house_id: houseId,
            match_status: undefined,
            contract_id: null,
            contract_status: null,
            payment_status: null,
            payment_link: null
          } as IMatch;
        }
        return Promise.reject(e);
      });
    },
    {
      enabled: Boolean(studentId && hostId && houseId) && enabled !== false
    }
  );
};

export const getStudentMatchesQueryKey = ({ userId }: { userId?: string }) => [
  "studentMatches",
  userId
];

export const useGetStudentMatches = (
  userId?: string,
  enabled: boolean = true,
  selectFn?: (res: TMatchData[]) => TMatchData[]
) => {
  return useQuery(
    getStudentMatchesQueryKey({ userId }),
    () => {
      if (!userId) {
        return;
      }

      return StudentAPI.getMatches(userId);
    },
    {
      enabled: Boolean(userId) && enabled,
      select: (data) =>
        typeof selectFn === "function" && Array.isArray(data)
          ? selectFn(data)
          : data
    }
  );
};

export const getStudentAggregatedMatchesQueryKey = ({
  userId
}: {
  userId?: string;
}) => ["studentAggregatedMatches", userId];

export const useGetStudentAggregatedMatches = (
  enabled: boolean = true,
  selectFn?: (res: TAggregatedMatchData[]) => TAggregatedMatchData[]
) => {
  const { currentUserId } = useContextHospi();

  return useInfiniteQuery({
    queryKey: getStudentAggregatedMatchesQueryKey({ userId: currentUserId }),
    queryFn: ({ pageParam }) => {
      return StudentAPI.getAggregatedMatches(
        currentUserId!,
        pageParam ?? null,
        SHOWN_MATCHES_FROM_STATUS
      );
    },
    enabled: Boolean(currentUserId) && enabled,
    getNextPageParam: (lastPage) => lastPage.next_batch_token ?? undefined,
    select: (data) =>
      typeof selectFn === "function"
        ? {
            ...data,
            pages: data.pages.map((p) => {
              return Array.isArray(p.data)
                ? {
                    ...p,
                    data: selectFn(p.data)
                  }
                : p;
            })
          }
        : data
  });
};

export const useShowInterest = () => {
  const queryClient = useQueryClient();

  return useMutation<
    unknown,
    unknown,
    { match: IBaseMatchData; message: string }
  >({
    mutationFn: (payload) =>
      StudentAPI.showInterest(payload.match, payload.message),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: getMatchQueryKey({
          studentId: variables.match.student_id,
          hostId: variables.match.host_id,
          houseId: variables.match.house_id
        })
      });
      queryClient.invalidateQueries({
        queryKey: getStudentMatchesQueryKey({
          userId: variables.match.student_id
        })
      });
    }
  });
};

export const useRejectInvite = () => {
  const queryClient = useQueryClient();

  return useMutation<unknown, unknown, IBaseMatchData>({
    mutationFn: (payload) => StudentAPI.rejectInvite(payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: getMatchQueryKey({
          studentId: variables.student_id,
          hostId: variables.host_id,
          houseId: variables.house_id
        })
      });
      queryClient.setQueryData<InfiniteData<TAggregatedMatchesResponse>>(
        getStudentAggregatedMatchesQueryKey({ userId: variables.student_id }),
        (oldData) => {
          if (oldData) {
            return {
              ...oldData,
              pages: oldData.pages.map((page) => ({
                ...page,
                data: page.data.reduce((acc, c) => {
                  if (
                    c.match.house_id === variables.house_id &&
                    c.match.host_id === variables.host_id
                  ) {
                    acc.push({
                      ...c,
                      match: {
                        ...c.match,
                        match_status: MatchStatusEnum.CHATTED,
                        offered_check_in_date: undefined,
                        offered_check_out_date: undefined
                      }
                    });
                  } else acc.push(c);
                  return acc;
                }, [] as TAggregatedMatchData[])
              }))
            };
          }
        }
      );
      queryClient.setQueryData<TMatchData[]>(
        getStudentMatchesQueryKey({ userId: variables.student_id }),
        (oldData) => {
          if (oldData) {
            return oldData.map((m) => {
              if (
                m.student_id === variables.student_id &&
                m.house_id === variables.house_id &&
                m.host_id === variables.host_id
              ) {
                return {
                  ...m,
                  match_status: MatchStatusEnum.CHATTED,
                  offered_check_in_date: undefined,
                  offered_check_out_date: undefined
                };
              } else return m;
            });
          }
        }
      );
    }
  });
};

export const useUpdateCheckoutDateByStudent = () => {
  const queryClient = useQueryClient();

  return useMutation<unknown, unknown, IUpdateCheckoutDatePayload>({
    mutationFn: (payload) => StudentAPI.updateCheckoutDate(payload),
    onSuccess: (_, { new_check_out_date, student_id, host_id, house_id }) => {
      queryClient.invalidateQueries({
        queryKey: getMatchQueryKey({
          studentId: student_id,
          hostId: host_id,
          houseId: house_id
        })
      });
      queryClient.invalidateQueries({
        queryKey: getStudentAggregatedMatchesQueryKey({
          userId: student_id
        })
      });
      queryClient.setQueryData<TMatchData[]>(
        getStudentMatchesQueryKey({ userId: student_id }),
        (oldData) => {
          if (oldData) {
            return oldData.map((m) => {
              if (
                m.student_id === student_id &&
                m.house_id === house_id &&
                m.host_id === host_id
              ) {
                const isPrematureArchiving =
                  !!new_check_out_date.length &&
                  new Date(new_check_out_date) < endOfDay(new Date()); // currently is always false

                return {
                  ...m,
                  check_out_date: new_check_out_date,
                  checkout_date_actor: "student",
                  match_status: isPrematureArchiving
                    ? MatchStatusEnum.ARCHIVED
                    : m.match_status,
                  premature_archive_date: isPrematureArchiving
                    ? isoDate(new Date())
                    : null
                };
              } else return m;
            });
          }
        }
      );
    }
  });
};

export const useSetStudentActions = () => {
  const queryClient = useQueryClient();
  const { currentUserId } = useContextHospi();

  return useMutation<
    unknown,
    unknown,
    { payload: TStudentActionExtrasPayload }
  >({
    mutationFn: ({ payload }) => StudentAPI.setActions(payload),
    onSuccess: (_, { payload }) => {
      queryClient.invalidateQueries({
        queryKey: getStudentMatchesQueryKey({ userId: currentUserId })
      });
      queryClient.setQueryData<IMatch>(
        getMatchQueryKey({
          studentId: currentUserId,
          hostId: payload.host_id,
          houseId: payload.house_id
        }),
        (oldData) => {
          if (oldData)
            return { ...oldData, student_action_extras: payload.data };
        }
      );
      queryClient.setQueryData<InfiniteData<TAggregatedMatchesResponse>>(
        getStudentAggregatedMatchesQueryKey({ userId: currentUserId }),
        (oldData) => {
          if (oldData) {
            return {
              ...oldData,
              pages: oldData.pages.map((page) => ({
                ...page,
                data: page.data.reduce((acc, c) => {
                  if (
                    c.match.house_id === payload.house_id &&
                    c.match.host_id === payload.host_id
                  ) {
                    acc.push({
                      ...c,
                      match: { ...c.match, student_action_extras: payload.data }
                    });
                  } else acc.push(c);
                  return acc;
                }, [] as TAggregatedMatchData[])
              }))
            };
          }
        }
      );
    }
  });
};

export const useCreateReview = () => {
  const queryClient = useQueryClient();

  return useMutation<
    TReview,
    unknown,
    {
      payload: Pick<TReview, "rating" | "review_text" | "house_id">;
      hostId: string;
    }
  >({
    mutationFn: ({ payload, hostId }) =>
      StudentAPI.createReview(payload, hostId),
    onSuccess: (data, { hostId }) => {
      queryClient.setQueryData<TReview[]>(
        getReviewsByHostQueryKey({ hostId }),
        (oldData) => (!!oldData ? [...oldData, data] : [data])
      );
      queryClient.setQueryData<TReview[]>(
        getReviewsByStudentQueryKey({ studentId: data.student_id }),
        (oldData) => (!!oldData ? [...oldData, data] : [data])
      );
    }
  });
};

export const useUpdateReview = () => {
  const queryClient = useQueryClient();

  return useMutation<
    unknown,
    unknown,
    {
      payload: Partial<TBaseReview> & { created_at: string };
      hostId: string;
      houseId: string;
      userId: string;
    }
  >({
    mutationFn: ({ payload, hostId }) =>
      StudentAPI.updateReview(payload, hostId),
    onSuccess: (_, { payload, hostId, houseId, userId }) => {
      queryClient.setQueryData<TReview[]>(
        getReviewsByHostQueryKey({ hostId }),
        (oldData) => {
          if (oldData) {
            const { created_at, rating, review_text } = payload;
            return oldData.map((r) => {
              if (
                r.created_at === created_at &&
                r.house_id === houseId &&
                r.student_id === userId
              ) {
                return {
                  ...r,
                  rating: rating ?? r.rating,
                  review_text: review_text ?? r.review_text
                };
              } else return r;
            });
          }
        }
      );
      queryClient.setQueryData<TReview[]>(
        getReviewsByStudentQueryKey({ studentId: userId }),
        (oldData) => {
          if (oldData) {
            const { created_at, rating, review_text } = payload;
            return oldData.map((r) => {
              if (
                r.created_at === created_at &&
                r.house_id === houseId &&
                r.host_id === hostId
              ) {
                return {
                  ...r,
                  rating: rating ?? r.rating,
                  review_text: review_text ?? r.review_text
                };
              } else return r;
            });
          }
        }
      );
    }
  });
};

// Searches
export const getSearchesQueryKey = ({ studentId }: { studentId?: string }) => [
  "searches",
  studentId
];

export const useGetSearches = (studentId?: string, enabled: boolean = true) => {
  return useQuery(
    getSearchesQueryKey({ studentId }),
    () => {
      if (!studentId) return;
      return StudentAPI.getSearches(studentId).then((result) =>
        Array.isArray(result) && result.length > 1
          ? result.sort((a, b) => sortByCreationTime(a, b, true))
          : result
      );
    },
    { enabled: Boolean(studentId) && enabled }
  );
};

export const useSaveSearchPreference = () => {
  const queryClient = useQueryClient();
  const { currentUserId } = useContextHospi();

  return useMutation<ISavedSearchPreference, unknown, ISearchPreferencePayload>(
    {
      mutationFn: (payload) =>
        StudentAPI.saveSearchPreference(payload, currentUserId),
      onSuccess: (data) => {
        queryClient.setQueryData<ISavedSearchPreference[]>(
          getSearchesQueryKey({ studentId: currentUserId }),
          (oldData) => {
            if (oldData) return [...oldData, data];
          }
        );
      }
    }
  );
};

export const useUpdateSavedSearch = () => {
  const queryClient = useQueryClient();
  const { currentUserId } = useContextHospi();

  return useMutation<
    ISavedSearchPreference,
    unknown,
    {
      payload: Partial<ISearchPreferencePayload>;
      createdAt: string;
    }
  >({
    mutationFn: ({ payload, createdAt }) =>
      StudentAPI.updateSearchPreference(createdAt, payload, currentUserId),
    onSuccess: (data, { createdAt }) => {
      queryClient.setQueryData<ISavedSearchPreference[]>(
        getSearchesQueryKey({ studentId: currentUserId }),
        (oldData) => {
          if (oldData)
            return oldData.map((s) =>
              s.created_at === createdAt ? { ...data } : s
            );
        }
      );
    }
  });
};

export const useDeleteSearchPreference = () => {
  const queryClient = useQueryClient();
  const { currentUserId } = useContextHospi();
  const { chosenSearch, chooseSearch } = useContextStudentSearches();

  return useMutation<unknown, unknown, string>({
    mutationFn: (createdAt) =>
      StudentAPI.deleteSearchPreference(createdAt, currentUserId),
    onSuccess: (_, createdAt) => {
      if (createdAt === chosenSearch?.created_at) chooseSearch(null);

      queryClient.setQueryData<ISavedSearchPreference[]>(
        getSearchesQueryKey({ studentId: currentUserId }),
        (oldData) => {
          if (oldData) return oldData.filter((s) => s.created_at !== createdAt);
        }
      );
    }
  });
};
